/**
 * Markdown rendering with sanitization
 */
import { messageImages } from './message-images.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { getTravelPlaceMapsUrl } from './travel-links.js';
import { fallbackLabel, stopReasonLabel } from './ui-text.js';

// Configure marked
marked.setOptions({
  breaks: true,
  gfm: true,
});

/**
 * Render markdown to sanitized HTML
 */
export function renderMarkdown(text) {
  if (!text) return '';
  
  const html = marked.parse(text);
  
  // Sanitize HTML to prevent XSS
  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'del', 'code', 'pre',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'blockquote',
      'a',
      'hr',
      // Таблицы: LLM регулярно отвечает ими; img намеренно НЕ разрешён
      // (источник — модель: трекинг-пиксели/утечка URL).
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
    // Этап 23: внутренние ссылки конспекта на сообщения-источники и этапы.
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|msg|stage):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  });

  if (typeof window === 'undefined') {
    return sanitized;
  }

  const template = document.createElement('template');
  template.innerHTML = sanitized;
  for (const link of template.content.querySelectorAll('a')) {
    const href = link.getAttribute('href') || '';
    // Этап 23 (реш. 21): `msg:<id>` — источник в конспекте, `stage:<id>` — этап в сводке.
    // Переход делает компонент по data-атрибуту; наружу такие ссылки не ведут.
    const internal = /^(msg|stage):(.+)$/i.exec(href);
    if (internal) {
      link.removeAttribute('href');
      link.setAttribute('role', 'button');
      link.setAttribute('tabindex', '0');
      link.setAttribute(internal[1].toLowerCase() === 'msg' ? 'data-source-message' : 'data-source-stage', internal[2]);
      link.classList.add('source-link');
      continue;
    }
    if (!/^(https?:|mailto:)/i.test(href)) {
      link.removeAttribute('href');
      continue;
    }
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  }

  // Таблица шире мобильного экрана скроллится внутри своей обёртки,
  // не растягивая ленту по горизонтали.
  for (const tableElement of template.content.querySelectorAll('table')) {
    const wrap = document.createElement('div');
    wrap.className = 'table-wrap';
    tableElement.replaceWith(wrap);
    wrap.appendChild(tableElement);
  }

  return template.innerHTML;
}

/**
 * Ссылки-источники конспекта этапа `[#n](msg:<id>)` → маркеры `[#n]`: в буфер обмена и
 * в Markdown-файл внутренние адреса не нужны (1.23.1, этап 23).
 */
export function stripSourceLinks(text) {
  return (text || '').replace(/\[#(\d+)\]\(msg:[^)]+\)/g, '[#$1]');
}

/**
 * Export chat to markdown format
 */
export function exportToMarkdown(title, messages, includeSystem = false, travelMeta = null, listMeta = null, extras = {}) {
  // Этап 23: этапы (номер и название конспекта) и сводка проекта первым разделом.
  const { stages = [], projectState = null, stateTitle = 'Состояние проекта' } = extras || {};
  let md = '';
  
  if (title) {
    md += `# ${title}\n\n`;
  }

  if (projectState) {
    md += `## ${stateTitle}\n\n`;
    md += projectState.hidden
      ? 'Сводка скрыта: проект начался до вашего приглашения.\n\n'
      : `${(projectState.summary || '').trim()}\n\n`;
    md += '---\n\n';
  }
  const stageOrder = new Map((stages || []).map((stage, index) => [stage.stageId, { stage, number: index + 1 }]));
  let fallbackStageNumber = 0;

  if (travelMeta?.chatType === 'travel') {
    md += '## Путевой чат\n\n';
    const profile = travelMeta.profile || {};
    const rows = [
      ['Город', profile.city],
      ['Даты', profile.dates],
      ['Интересы', Array.isArray(profile.interests) ? profile.interests.join(', ') : ''],
      ['Темп', profile.pace],
      ['Транспорт', profile.transport],
      ['Бюджет', profile.budget],
      ['Заметки', profile.notes],
    ].filter(([, value]) => value);
    for (const [label, value] of rows) {
      md += `- ${label}: ${value}\n`;
    }
    if (travelMeta.places?.length) {
      md += '\n### Места из Яндекс Карт\n\n';
      for (const place of travelMeta.places) {
        const placeUrl = getTravelPlaceMapsUrl(place);
        md += placeUrl ? `- [${place.title}](${placeUrl})\n` : `- ${place.title}\n`;
        if (place.address) md += `  - Адрес: ${place.address}\n`;
        if (typeof place.latitude === 'number' && typeof place.longitude === 'number') {
          md += `  - Координаты: ${place.latitude}, ${place.longitude}\n`;
        }
        if (place.selectionReason) md += `  - Почему выбрано: ${place.selectionReason}\n`;
        if (place.note) md += `  - Заметка: ${place.note}\n`;
      }
    }
    md += '\n---\n\n';
  }
  
  if (listMeta?.chatType === 'list' && listMeta.items?.length) {
    md += '## Список\n\n';
    for (const item of listMeta.items) {
      md += `- [${item.isDone ? 'x' : ' '}] ${item.text}\n`;
    }
    md += '\n---\n\n';
  }

  for (const msg of messages) {
    if (msg.role === 'system' && !includeSystem) continue;

    if (msg.role === 'summary') {
      const stageId = (msg.requestId || '').startsWith('stage:') ? msg.requestId.slice(6) : null;
      const known = stageId ? stageOrder.get(stageId) : null;
      fallbackStageNumber += 1;
      const number = known ? known.number : fallbackStageNumber;
      const stage = known ? known.stage : null;
      md += `## Этап ${number}${stage?.title ? `. ${stage.title}` : ''}\n\n`;
      if (stage) {
        const range = [stage.fromTs, stage.toTs]
          .map((ts) => (ts ? new Date(ts).toLocaleDateString('ru-RU') : ''))
          .filter(Boolean);
        md += `_${stage.messageCount ?? 0} сообщений · ${[...new Set(range)].join(' — ')}_\n\n`;
      }
      md += msg.summaryHidden
        ? 'Конспект скрыт: этап начался до вашего приглашения.\n\n'
        : `${stripSourceLinks(msg.text)}\n\n`;
      md += '---\n\n';
      continue;
    }
    
    const roleLabel = {
      'user': '**Вы:**',
      'assistant': '**Ассистент:**',
      'system': '**Система:**',
    }[msg.role] || '**Сообщение:**';
    
    md += `${roleLabel}\n\n`;
    const exportImages = messageImages(msg);
    for (let i = 0; i < exportImages.length; i += 1) {
      md += '![Изображение из чата](вложение-недоступно-в-export)\n\n';
    }
    if (msg.text) {
      md += `${msg.text}\n\n`;
    }
    if (msg.role === 'assistant' && msg.fallbackFrom) {
      // Этап 25.2: ответило резервное звено цепочки провайдеров.
      md += `_${fallbackLabel(msg.model)}_\n\n`;
    }
    if (msg.role === 'assistant' && msg.stopReason) {
      // 1.27.0: ответ не дописан — в файле он не должен выглядеть полным.
      md += `_${stopReasonLabel(msg.stopReason)}_\n\n`;
    }
    md += '---\n\n';
  }
  
  return md;
}

export function exportExchangeToMarkdown(title, userMessage, assistantMessage) {
  const items = [];
  if (userMessage) {
    items.push(userMessage);
  }
  if (assistantMessage) {
    items.push(assistantMessage);
  }
  return exportToMarkdown(title, items, false);
}

/**
 * Download text as file
 */
export function downloadFile(content, filename, mimeType = 'text/markdown') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
